import { FC, useContext } from 'react';

import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import {
  AllBookingsModal,
  BookingModal,
  ChangeHostModal,
  DepositModal,
  GeneratePinModal,
  HostCommentsModal,
  HostMessageModal,
  HostTimeDownModal,
  ManageHostGroupModal,
  ManageHostModal,
  RunFileModal,
  StartSeanceModal,
  StopClientSessionModal,
} from 'modals';
import notification from 'services/notificationService';

import { GenerateQRModal } from '../../modals/GenerateQRModal';
import { HOST_MODALS_DATA, HostsModalsDataResponse } from './schema';
import { EHostModalType, IHostModalsProps } from './types';

export const HostModals: FC<IHostModalsProps> = ({
  type,
  setType,
  penalty,
  selectedHostsIds,
  bookingIdForEdit,
  setBookingIdForEdit,
  refetch,
  groupId,
  hostCoords,
  unselectHosts,
  stopPolling,
  statedPoling,
  setIsDepositOpen,
  setIsDefaultClient,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { data } = useQuery<HostsModalsDataResponse>(HOST_MODALS_DATA, {});

  const hosts = data?.hostsOverview ?? [];

  switch (type) {
    case EHostModalType.RUN_FILE:
      if (!selectedHostsIds) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <RunFileModal
          hostsIds={selectedHostsIds}
          onClose={() => setType(EHostModalType.NONE)}
        />
      );
    case EHostModalType.COMMENTS: {
      if (selectedHostsIds?.length !== 1) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <HostCommentsModal
          hostId={selectedHostsIds[0]}
          onClose={() => setType(EHostModalType.NONE)}
        />
      );
    }
    case EHostModalType.PENALTY: {
      if (!penalty || selectedHostsIds?.length !== 1) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <HostTimeDownModal
          hostId={selectedHostsIds[0]}
          clientPenalty={penalty}
          onClose={() => setType(EHostModalType.NONE)}
        />
      );
    }
    case EHostModalType.SHOP: {
      if (!permissions?.has('payment_create')) {
        setType(EHostModalType.NONE);
        return null;
      }

      const host = hosts.find((host) => host.id === selectedHostsIds[0]);

      if (host?.in_service) {
        setType(EHostModalType.NONE);
        return null;
      }

      if (host?.locked) {
        setType(EHostModalType.NONE);
        notification.info({
          t,
          type: 'warning',
          title: t('session.start.locked'),
        });

        setType(EHostModalType.NONE);
        return null;
      }

      const clientsUUIDsWithSession =
        hosts.reduce<Array<string>>((acc, host) => {
          const clientUUID = host.user?.uuid;

          if (clientUUID) {
            acc.push(clientUUID);
          }

          return acc;
        }, []) ?? [];

      return (
        <StartSeanceModal
          onClose={() => {
            refetch();
            unselectHosts();
            setType(EHostModalType.NONE);
          }}
          hostIds={selectedHostsIds}
          clientsUUIDsWithSession={clientsUUIDsWithSession}
          setIsDepositOpen={setIsDepositOpen}
          setIsDefaultClient={setIsDefaultClient}
          bookingId={bookingIdForEdit}
          hosts={data}
        />
      );
    }
    case EHostModalType.MANAGE_HOST: {
      if (selectedHostsIds?.length > 1) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <ManageHostModal
          hostId={selectedHostsIds[0]}
          closeModal={() => setType(EHostModalType.NONE)}
          hostCoords={hostCoords}
          onSuccess={() => refetch()}
        />
      );
    }

    case EHostModalType.SEND_MESSAGE: {
      if (!selectedHostsIds) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <HostMessageModal
          hostsIds={selectedHostsIds}
          onClose={() => setType(EHostModalType.NONE)}
        />
      );
    }

    case EHostModalType.MANAGE_HOST_GROUP: {
      return (
        <ManageHostGroupModal
          groupId={groupId}
          closeModal={() => setType(EHostModalType.NONE)}
          onSuccess={refetch}
        />
      );
    }

    case EHostModalType.STOP_CLIENT_SESSION: {
      const hostData = hosts.find((host) => host.id === selectedHostsIds[0]);

      if (selectedHostsIds?.length !== 1 || !hostData?.client_sessions[0]) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <StopClientSessionModal
          sessionId={hostData.client_sessions[0].id}
          closeModal={() => setType(EHostModalType.NONE)}
          isPostPayment={hostData.client_sessions[0].postpaid}
          refetch={refetch}
          stopPolling={stopPolling}
          statedPoling={statedPoling}
        />
      );
    }

    case EHostModalType.CLIENT_DEPOSIT: {
      if (
        selectedHostsIds?.length !== 1 ||
        !permissions?.has('payment_create')
      ) {
        setType(EHostModalType.NONE);
        return null;
      }

      const host = hosts.find((h) => h.id === selectedHostsIds[0]);

      return (
        <DepositModal
          defaultClientUUID={host?.user?.uuid}
          onClose={() => setType(EHostModalType.NONE)}
        />
      );
    }

    case EHostModalType.MOVE_SESSION: {
      if (selectedHostsIds?.length !== 1) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <ChangeHostModal
          hosts={data}
          hostId={selectedHostsIds[0]}
          closeModal={() => setType(EHostModalType.NONE)}
        />
      );
    }

    case EHostModalType.ALL_BOOKINGS: {
      if (selectedHostsIds?.length !== 1) {
        setType(EHostModalType.NONE);
        return null;
      }

      return (
        <AllBookingsModal
          hostId={selectedHostsIds[0]}
          editBooking={(id) => {
            setBookingIdForEdit(id);
            setType(EHostModalType.BOOKING);
          }}
          onClose={() => setType(EHostModalType.NONE)}
        />
      );
    }

    case EHostModalType.BOOKING: {
      return (
        <BookingModal
          onClose={() => {
            setType(EHostModalType.NONE);
          }}
          bookingId={bookingIdForEdit}
          selectedHostsIds={selectedHostsIds}
          setIsDepositOpen={setIsDepositOpen}
          setIsDefaultClient={setIsDefaultClient}
          selectedTime={dayjs().format('HH:mm')}
        />
      );
    }

    case EHostModalType.GENERATE_PIN: {
      if (!selectedHostsIds?.[0]) return null;

      return (
        <GeneratePinModal
          hostId={selectedHostsIds[0]}
          hosts={data}
          onClose={() => setType(EHostModalType.NONE)}
        />
      );
    }

    case EHostModalType.GENERATE_QR:
      if (selectedHostsIds.length === 1) {
        return (
          <GenerateQRModal
            close={() => setType(EHostModalType.NONE)}
            hostId={selectedHostsIds[0]}
            hosts={data}
          />
        );
      } else return null;

    case EHostModalType.NONE:
    default:
      return null;
  }
};
